import { useCallback, useEffect, useRef, useState } from 'react'
import { PAGE_MAX } from '@/lib/api'

// ─── Single resource ──────────────────────────────────────────────────────────

export interface ApiResourceState<T> {
  data: T | null
  /**
   * There is nothing on screen yet — the caller must draw a placeholder. This is
   * true for the first load and whenever `deps` change (a different question is
   * being asked, so the answer on screen no longer belongs under the new label).
   */
  loading: boolean
  /**
   * The SAME payload is being fetched again underneath what is already drawn.
   * Callers should leave the content up: see the note on `refresh` below.
   */
  refreshing: boolean
  error: string | null
  /**
   * Awaitable, so a row action's spinner can cover the refetch and not just the
   * write. A handler that settles before the list has been re-read releases the
   * spinner while the stale row is still on screen — a deleted photo reappears
   * as though nothing happened.
   */
  refresh: () => Promise<void>
}

function message(e: unknown): string {
  if (e instanceof Error) return e.message
  return String(e)
}

/**
 * Fetch one payload once (and on demand). `deps` behaves like a useEffect dep
 * array — change it to refetch. The fetcher is intentionally NOT a dependency,
 * so callers can pass an inline arrow without causing a loop.
 *
 * `refresh()` re-fetches IN PLACE: it sets `refreshing`, never `loading`, so the
 * data already on screen stays mounted. This is not a nicety. Every write in the
 * app is followed by a refetch (check a customer in, retire a service, take a
 * payment), and flipping `loading` unmounts the whole screen behind a spinner and
 * remounts it a moment later — which resets scroll position and reads to an
 * operator as the page having reloaded. In a SPA it never should. The one thing
 * that legitimately blanks the screen is a `deps` change, because then what is
 * drawn answers a question nobody asked any more.
 */
export function useApiResource<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): ApiResourceState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  // Guards against a slow earlier request overwriting a newer one.
  const runId = useRef(0)
  // Read inside load() to decide whether there is anything to keep on screen —
  // state would be captured stale by the memoised callback.
  const dataRef = useRef<T | null>(null)

  const load = useCallback(async (inPlace: boolean) => {
    const id = ++runId.current
    // A refresh with nothing rendered yet is just the initial load wearing a
    // different name — keep showing the placeholder rather than an empty screen.
    const quiet = inPlace && dataRef.current !== null
    if (quiet) setRefreshing(true)
    else setLoading(true)
    try {
      const result = await fetcherRef.current()
      if (id !== runId.current) return
      dataRef.current = result
      setData(result)
      setError(null)
    } catch (e) {
      if (id !== runId.current) return
      setError(message(e))
    } finally {
      if (id === runId.current) {
        setLoading(false)
        setRefreshing(false)
      }
    }
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false) }, [load, ...deps])

  const refresh = useCallback(() => load(true), [load])

  return { data, loading, refreshing, error, refresh }
}

// ─── Paginated list ───────────────────────────────────────────────────────────

export interface PagedResponse<T> {
  items: T[]
  total: number | null
}

export interface ApiListState<T> {
  items: T[]
  total: number | null
  /** Nothing listed yet — draw a placeholder. Never set by `refresh()`. */
  loading: boolean
  loadingMore: boolean
  /** The first page is being re-fetched under the rows already listed. */
  refreshing: boolean
  error: string | null
  hasMore: boolean
  loadMore: () => void
  /** Awaitable — see the note on ApiResourceState.refresh. */
  refresh: () => Promise<void>
}

/**
 * Offset-paginated list with an explicit "load more". Changing `deps` (a filter,
 * a search term) resets to offset 0 — never appends across filter changes, which
 * would mix two different result sets in one list.
 *
 * `refresh()` re-reads the first page in place: `refreshing`, never `loading`, so
 * the rows on screen stay put. See the note on useApiResource for why — a screen
 * that blanks itself after every write reads as a page reload.
 */
export function useApiList<T>(
  fetchPage: (offset: number, limit: number) => Promise<PagedResponse<T>>,
  deps: unknown[] = [],
  // Match the API cap — asking for less just means more round trips for the
  // same data. Callers can pass a smaller page where a short list reads better.
  pageSize = PAGE_MAX,
): ApiListState<T> {
  const [items, setItems] = useState<T[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fetchRef = useRef(fetchPage)
  fetchRef.current = fetchPage
  const runId = useRef(0)
  const itemsRef = useRef<T[]>([])

  const loadPage = useCallback(async (
    offset: number,
    mode: 'reset' | 'append' | 'refresh',
  ) => {
    const append = mode === 'append'
    const id = append ? runId.current : ++runId.current
    // As in useApiResource: a refresh over an empty list is still a first load.
    const quiet = mode === 'refresh' && itemsRef.current.length > 0
    if (append) setLoadingMore(true)
    else if (quiet) setRefreshing(true)
    else setLoading(true)
    try {
      const page = await fetchRef.current(offset, pageSize)
      if (id !== runId.current) return
      itemsRef.current = append ? [...itemsRef.current, ...page.items] : page.items
      setItems((prev) => (append ? [...prev, ...page.items] : page.items))
      setTotal(page.total)
      setError(null)
    } catch (e) {
      if (id !== runId.current) return
      setError(message(e))
      // A failed refresh leaves the rows alone: the operator was reading them a
      // moment ago and they are still the last thing we know to be true. Only a
      // reset clears, because there the old rows answer a superseded query.
      if (mode === 'reset') {
        itemsRef.current = []
        setItems([])
      }
    } finally {
      if (id === runId.current) {
        setLoading(false)
        setLoadingMore(false)
        setRefreshing(false)
      }
    }
  }, [pageSize])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPage(0, 'reset') }, [loadPage, ...deps])

  const loadMore = useCallback(() => {
    if (loadingMore || loading) return
    loadPage(items.length, 'append')
  }, [loadPage, items.length, loading, loadingMore])

  const refresh = useCallback(() => loadPage(0, 'refresh'), [loadPage])

  const hasMore = total !== null ? items.length < total : false

  return { items, total, loading, loadingMore, refreshing, error, hasMore, loadMore, refresh }
}
